import { observer } from 'mobx-react';
import { CreateConversationForm } from 'Modules/conversation/createConversation/components/createConversationForm';
import { useConversationHasMessages } from 'Modules/conversation/hooks/useConversationHasMessages';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import React, { useEffect } from 'react';
import { routeChecks } from 'Utils/routeUtils';
import { cn } from '~/lib/utils';
import { ChatFooter } from '../ChatFooter';
import { PreviewConversationContent } from './PreviewConversationContent';
/**
 * Layout component for the new conversation creation interface.
 * This component focuses purely on layout and presentation.
 * All business logic is handled by CreateConversationForm.
 *
 * Warning
 * Do NOT wrap this in useMemo
 * - Mobx observer riles on reading observables during render to track dependencies.
 */

export const ChatNewConversationLayout = observer(() => {
  const {
    selectedContacts,
    previewConversationId,
    isAdHocGroupCreation,
    setPreviewConversationId,
    setIsAdHocGroupCreation,
  } = useConversationStore();

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      setPreviewConversationId(null);
      setIsAdHocGroupCreation(false);
    };
  }, [setIsAdHocGroupCreation, setPreviewConversationId]);

  const scrollToBottom = React.useCallback(() => {
    if (scrollContainerRef.current) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop =
            scrollContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, []);

  const { hasMessages, totalCount } =
    useConversationHasMessages(previewConversationId);

  React.useEffect(() => {
    if (previewConversationId && !isAdHocGroupCreation && totalCount > 0) {
      scrollToBottom();
    }
  }, [previewConversationId, isAdHocGroupCreation, totalCount, scrollToBottom]);

  const showEmpty = routeChecks.isNewConversationPage() && !hasMessages;

  return (
    <div className={cn('flex flex-col h-full justify-between')}>
      <div>
        <CreateConversationForm />
      </div>

      <div
        className={cn(
          'grid grid-cols-1 justify-center h-full overflow-y-auto',
          hasMessages ? 'items-end' : 'items-center'
        )}
      >
        <PreviewConversationContent
          previewConversationId={previewConversationId}
          isContactSelected={selectedContacts.length > 0}
          showEmpty={showEmpty}
          scrollContainerRef={scrollContainerRef}
        />
      </div>

      <ChatFooter />
    </div>
  );
});
